<template>
    <BreadCrumbsWithButtons
        :buttons="buttons"
        :pdfOptions="pdfOptions.simple"
    />
    <a href="#" id="section1" ref="section1"></a>
    <OfferInformation
        v-if="client && location"
        :client="client"
        :location="location"
        :offer="offer"
        :offerId="offerId"
        show-area-visible-checkbox
        :area-visible-checkbox-disabled="!hasCeilingSetupArea"
        @update="updateOfferData"
        @offer-buttons-in-view="isOfferButtonsInView = $event"
    >
    </OfferInformation>
    <CeilingSetup v-model="ceilingSetup" class="mb-4" />

    <a href="#" id="section2" ref="section2"></a>
    <ProductTable
        v-if="loadProductTable"
        :offer="offer"
        :offered-products="offeredProducts ?? []"
        :offer-type="{ type: 'CEILING', productCategories: ['TAVAN'] }"
        :headers="productTableHeaders"
        :high-priority-products="['1101.00.61']"
        @sort-pdf-by-number="setPDFCustomSorting"
        @update-products="updateOfferProducts"
        @update-offer="updateOfferData"
        @selected-price-list="updateOfferPriceList"
        :price-list-type="this.$store.state.PRICE_LIST_TYPES.GENERAL.value"
        :showAddServicePIF="false"
    >
    </ProductTable>
</template>

<script>
import navMixin from "@/mixins/navMixin";
import OfferInformation from "@/components/offer/OfferInformation.vue";
import offerMixin from "@/mixins/offerMixin";
import ProductTable from "@/components/offer/ProductTable.vue";
import BreadCrumbsWithButtons from "@/components/common/BreadCrumbsWithButtons.vue";
import CeilingSetup from "@/components/offer/CeilingSetup.vue";
import { showAlertModal } from "@/utils/utils";
import { ceilingProducts, quantityAliases } from "@/CEILING/ceilingProducts.js";
import emitter from "@/utils/emitter";
import apiClient from "@/utils/apiClient";

export default {
    name: "OfferCeiling",
    mixins: [navMixin, offerMixin],
    components: {
        BreadCrumbsWithButtons,
        ProductTable,
        OfferInformation,
        CeilingSetup,
    },
    data() {
        return {
            productsNeedRecalculation: false,
            isHydratingCeilingSetup: false,
            tavanPriceListItems: [],
            products: [],
            ceilingSetup: {
                area: "",
                floors: "",
            },
            sidebarLinks: [
                {
                    name: "Informații",
                    link: "#section1",
                    id: "section1",
                },
                {
                    name: "Produse",
                    link: "#section2",
                    id: "section2",
                },
            ],
            productTableHeaders: [
                {
                    title: "Nr.",
                    key: "number",
                    align: "center",
                    show: false,
                    width: "100px",
                },
                {
                    title: "Cod articol",
                    key: "product_code",
                    align: "center",
                    show: true,
                    width: "140px",
                },
                {
                    title: "Nume articol",
                    key: "product_name",
                    align: "start",
                    show: true,
                },
                {
                    title: "Categorie",
                    key: "assigned_sub_category",
                    align: "center",
                    show: false,
                },
                {
                    title: "Cantitate",
                    key: "quantity",
                    align: "center",
                    show: true,
                    width: "130px",
                },
                {
                    title: "UM",
                    key: "measurement_unit",
                    align: "center",
                    show: true,
                    width: "70px",
                },
                {
                    title: "Preț listă cu TVA",
                    key: "price",
                    align: "end",
                    show: true,
                },
                {
                    title: "Discount",
                    key: "discount",
                    align: "center",
                    show: true,
                    width: "120px",
                },
                {
                    title: "Preț final cu TVA",
                    key: "final_price",
                    align: "end",
                    show: true,
                },
                {
                    title: "Valoare finală cu TVA",
                    key: "final_total_price",
                    align: "end",
                    show: true,
                },
            ],
        };
    },
    created() {
        this.$router.beforeEach((to, from, next) => {
            if (
                from.name === "OfferCeiling" &&
                to.name !== "OfferCeiling" &&
                this.isProductsChanged === true
            ) {
                // Replace 'FormPage' with your form's route name
                const shouldLeave = this.checkFormBeforeLeave();
                if (!shouldLeave) {
                    next(false); // Prevent route change
                } else {
                    this.isProductsChanged = false;
                    next(); // Proceed with the route change
                }
            } else {
                next(); // Proceed with the route change for routes other than the form page
            }
        });
    },
    computed: {
        hasCeilingSetupArea() {
            const area = Number(this.ceilingSetup?.area);
            return Number.isFinite(area) && area > 0;
        },
    },
    async mounted() {
        this.$store.state.sidebarLinks = this.sidebarLinks;
        // Load the GENERAL price list items!
        const priceListResponse = await this.fetchPriceListProducts(
            this.$store.state.PRICE_LIST_TYPES.GENERAL.value
        );
        this.products = Array.isArray(priceListResponse.items)
            ? priceListResponse.items
            : priceListResponse;

        // FILTER only TAVAN
        this.tavanPriceListItems = this.products.filter((p) =>
            p.category_assignments?.some(
                (ca) =>
                    ca.main_category?.name === "TAVAN" ||
                    ca.sub_category?.name === "TAVAN"
            )
        );
        // Check if the offer ID is available before attempting to load ceiling setup.
        // If not, log a warning and skip the fetch operation.
        if (!this.offerId) {
            console.warn(
                "Offer ID not available yet. Skipping ceiling setup fetch."
            );
            return;
        }

        // Send GET request to fetch the existing ceiling setup for the current offer.
        try {
            const response = await apiClient.get(
                `/api/get-ceiling-setup/${this.offerId}/`
            );
            if (response && response.data) {
                this.setCeilingSetupFromSource(response.data, {
                    markRecalculation: false,
                });
            }
        } catch (error) {
            if (error.response?.status === 404) {
                // No existing ceiling setup was found for this offer.
                // This is expected behavior and does not require user notification.
                console.info(
                    "No ceiling setup found for this offer. Skipping."
                );
            } else {
                // Other errors (e.g., network issues or server errors) should be logged for debugging.
                console.error("Error fetching ceiling setup", error);
            }
        }
    },
    methods: {
        setCeilingSetupFromSource(setup = {}, options = {}) {
            const { markRecalculation = false } = options;
            this.isHydratingCeilingSetup = true;
            this.ceilingSetup = {
                area: setup?.area ?? "",
                floors: setup?.floors ?? "",
            };
            this.productsNeedRecalculation = !!markRecalculation;
            this.$nextTick(() => {
                this.isHydratingCeilingSetup = false;
            });
        },
        /**
         * @file OfferCeiling.vue
         * @deprecated This component implements offer-specific ceiling logic diverging
         *             from the general product engine. Do NOT reuse in other flows.
         * @todo     Refactor: extract shared ceiling-product logic .
         */

        /**
         * Normalizes a price-list row price (API uses `price`, not `price_with_vat`).
         * @param {Object|null|undefined} item
         * @returns {number}
         */
        resolveListPrice(item) {
            if (!item) {
                return 0;
            }
            const raw =
                item.price ?? item.price_with_vat ?? item.list_price ?? 0;
            const parsed = Number(raw);
            return Number.isFinite(parsed) ? parsed : 0;
        },

        /**
         * Retrieve the effective price for a given product code.
         * Falls back from offeredProducts -> TAVAN price list -> general price list -> 0.
         * @param {string} code - The product_code identifier.
         * @returns {number} - The resolved price including VAT or 0.
         */
        getPrice(code) {
            const existing = this.offeredProducts?.find(
                (p) => p.product_code === code
            );
            const existingPrice = Number(existing?.price);
            if (Number.isFinite(existingPrice) && existingPrice > 0) {
                return existingPrice;
            }

            const tavanMatch = this.tavanPriceListItems.find(
                (p) => p.product_code === code
            );
            const generalMatch = this.products.find(
                (p) => p.product_code === code
            );
            return (
                this.resolveListPrice(tavanMatch) ||
                this.resolveListPrice(generalMatch) ||
                0
            );
        },

        /**
         * Build per-product formula context (budget G column, unit price, area).
         * @param {Object} product - ceilingProducts entry
         * @param {Object} contextBase - area, floors, pricePerM2, etc.
         * @returns {Object}
         */
        buildProductCalcContext(product, contextBase) {
            const areaM2 = Number(contextBase.area_m2 ?? contextBase.area ?? 0);
            const pricePerM2 = Number(contextBase.pricePerM2 ?? 0);
            const percent = Number(product.specifications?.percent ?? 0);
            const unitPrice = this.getPrice(product.product_code);
            return {
                ...contextBase,
                getProductPrice: (productCode) => this.getPrice(productCode),
                priceForCalc: unitPrice,
                unit_price: unitPrice,
                product_total_value:
                    (percent / 100) * areaM2 * pricePerM2,
            };
        },

        /**
         * @param {unknown} value
         * @returns {number}
         */
        toFiniteQuantity(value) {
            const n = Number(value);
            if (!Number.isFinite(n) || n < 0) {
                return 0;
            }
            return Number(n.toFixed(2));
        },

        /**
         * Evaluate a product requirement with a formula or fixed-per-offer.
         * Interpolates variables, supports cross-product aliases, and applies rounding.
         * @param {Object} requirement - The requirement definition (expression, vars, rounding).
         * @param {Object} context - Current offer context (area, floors, etc.).
         * @param {Object} resolvedQuantities - Memo of already-calculated quantities.
         * @param {Array<Object>} allProducts - List of ceilingProducts definitions.
         * @param {Object} quantityAliases - Map of alias vars to product codes.
         * @returns {number} - The evaluated quantity (rounded per rules).
         */
        evaluateRequirement(
            requirement,
            context,
            resolvedQuantities,
            allProducts,
            quantityAliases
        ) {
            if (!requirement) return 0;

            if (requirement.method === "fixedPerOffer") {
                return 1;
            }

            if (requirement.method !== "formula" || !requirement.expression) {
                return 0;
            }

            let expr = requirement.expression;
            requirement.variables?.forEach((variable) => {
                let value;
                if (Object.prototype.hasOwnProperty.call(context, variable)) {
                    value = context[variable];
                } else if (quantityAliases[variable]) {
                    const code = quantityAliases[variable];
                    const refProduct = allProducts.find(
                        (p) => p.product_code === code
                    );
                    value = refProduct
                        ? this.resolveProductQuantity(
                              refProduct,
                              context,
                              resolvedQuantities,
                              allProducts,
                              quantityAliases
                          )
                        : 0;
                } else {
                    console.warn(`Unknown variable: ${variable}, assuming 0`);
                    value = 0;
                }
                expr = expr.replaceAll(
                    variable,
                    String(this.toFiniteQuantity(value))
                );
            });

            const unitPrice = Number(
                context.unit_price ?? context.priceForCalc ?? 0
            );
            if (
                requirement.variables?.includes("unit_price") &&
                unitPrice <= 0
            ) {
                return 0;
            }

            let result = 0;
            try {
                result = new Function(`return ${expr}`)();
            } catch (e) {
                console.error(`Error evaluating: ${expr}`, e);
            }

            if (requirement.rounding?.toUpperCase() === "CEILING") {
                const rawStep = Number(requirement.step);
                const step = isNaN(rawStep) || rawStep <= 0 ? 1 : rawStep;
                result = Math.ceil(result / step) * step;
            } else if (requirement.rounding?.toUpperCase() === "ROUNDUP") {
                result = Math.ceil(result);
            }
            this.productsNeedRecalculation = false;
            return this.toFiniteQuantity(result);
        },

        /**
         * Recursively resolve total quantity for a product, handling dependencies.
         * Uses evaluateRequirement for each requirement, or direct specs if none.
         * @param {Object} product - The ceilingProducts entry.
         * @param {Object} context - Contextual values for calculation.
         * @param {Object} resolvedQuantities - Memo object to avoid re-computation.
         * @param {Array<Object>} allProducts - Array of all ceilingProducts.
         * @param {Object} quantityAliases - Alias map for cross-product refs.
         * @returns {number} - Total calculated quantity for the product.
         */
        resolveProductQuantity(
            product,
            context,
            resolvedQuantities,
            allProducts,
            quantityAliases
        ) {
            if (
                Object.prototype.hasOwnProperty.call(
                    resolvedQuantities,
                    product.product_code
                )
            ) {
                return resolvedQuantities[product.product_code];
            }

            const productCtx = this.buildProductCalcContext(product, context);

            let total = 0;
            if (!product.requirements || !product.requirements.length) {
                const spec = product.specifications || {};
                if (spec.manualQty) {
                    total = 0;
                } else if (spec.fixedPerFloor) {
                    total = Number(productCtx.floors ?? productCtx.nr_etaje) || 0;
                } else if (spec.fixedPerOffer) {
                    total = 1;
                } else if (spec.percent) {
                    const priceForCalc = Number(productCtx.priceForCalc ?? 0);
                    if (priceForCalc > 0) {
                        total =
                            productCtx.product_total_value / priceForCalc;
                    }
                }
                resolvedQuantities[product.product_code] =
                    this.toFiniteQuantity(total);

                return resolvedQuantities[product.product_code];
            }

            for (const req of product.requirements) {
                total += this.evaluateRequirement(
                    req,
                    productCtx,
                    resolvedQuantities,
                    allProducts,
                    quantityAliases
                );
            }

            resolvedQuantities[product.product_code] =
                this.toFiniteQuantity(total);

            return resolvedQuantities[product.product_code];
        },

        /**
         * Main entry: Recalculate and update all offeredProducts quantities.
         * Executes a two-pass algorithm: independents first, then dependents.
         * Emits loading overlay and shows success or error alerts.
         * @async
         * @returns {Promise<void>}
         */
        async processRequiredProducts() {
            emitter.emit("trigger-overlay");
            this.offeredProducts = [];
            const { area, floors, offers } = this.ceilingSetup;
            // Validate numeric and positive values
            if (!area || area <= 0 || !floors || floors <= 0) {
                return showAlertModal(
                    this.$store,
                    "Completați corect toate câmpurile: Suprafață (>0), Etaje (>0)",
                    "warning",
                    4000
                );
            }

            if (!this.tavanPriceListItems.length)
                return showAlertModal(
                    this.$store,
                    "Nu s-au încărcat produsele din lista de prețuri.",
                    "danger",
                    6000
                );

            this.offeredProducts = this.offeredProducts?.length
                ? this.offeredProducts
                : this.products.map((p) => ({ ...p, quantity: 0 }));
            const existingProducts = this.offeredProducts;
            const updatedProducts = [];
            const resolvedQuantities = {};
            const missingFromPricelist = [];
            const contextBase = {
                area: Number(area),
                floors: Number(floors),
                offers: Number(offers),
                area_m2: Number(area),
                nr_etaje: Number(floors),
                nr_oferta: Number(offers),
                pricePerM2: 385,
                panel_unit_m2: 2.4,
                unit_length: 1,
                pipe_density_per_m2: 1.42,
            };

            const independent = [],
                dependent = [];
            for (const p of ceilingProducts) {
                const hasAlias = p.requirements?.some((r) =>
                    r.variables?.some((v) => v in quantityAliases)
                );
                (hasAlias ? dependent : independent).push(p);
            }

            const resolveSingle = (cp) => {
                const price = this.getPrice(cp.product_code);
                const discount =
                    existingProducts.find(
                        (p) => p.product_code === cp.product_code
                    )?.discount ?? 0;
                const ctx = this.buildProductCalcContext(cp, contextBase);

                let qty = this.resolveProductQuantity(
                    cp,
                    ctx,
                    resolvedQuantities,
                    ceilingProducts,
                    quantityAliases
                );
                qty = this.toFiniteQuantity(qty);
                const inPriceList = this.products.some(
                    (p) => p.product_code === cp.product_code
                );
                if (!inPriceList) {
                    qty = 0;
                    missingFromPricelist.push({
                        product_code: cp.product_code,
                        name: cp.name,
                    });
                }
                const finalPrice = Number(
                    (price * (1 - discount / 100)).toFixed(2)
                );
                const existing = existingProducts.find(
                    (p) => p.product_code === cp.product_code
                );
                const existingRest = { ...(existing || {}) };
                delete existingRest.id;
                updatedProducts.push({
                    ...existingRest,
                    product_code: cp.product_code,
                    product_name: cp.name,
                    quantity: qty,
                    measurement_unit: cp.unit ?? "buc",
                    price,
                    discount,
                    category: existingRest.category || "PRINCIPALE",
                    assigned_sub_category:
                        existingRest.assigned_sub_category || "PRINCIPALE",
                    final_price: finalPrice,
                    final_total_price: Number((finalPrice * qty).toFixed(2)),
                    manual_added: false,
                });
            };

            independent.forEach(resolveSingle);
            dependent.forEach(resolveSingle);
            this.offeredProducts = [...updatedProducts];
            this.isProductsChanged = true;

            setTimeout(() => {
                emitter.emit("trigger-overlay");
                // emitting event for setting showOnlyOfferQty on true in ProductTable
                emitter.emit("set-products-with-quantity-true");
                this.showPostCalculationAlerts({
                    missingProducts: missingFromPricelist,
                });
            }, 50);
        },

        checkFormBeforeLeave() {
            return confirm(
                "Sunteți sigur că doriți să părăsiți formularul? Modificările nesalvate vor fi pierdute."
            );
        },
    },
    watch: {
        "ceilingSetup.area": function (newVal) {
            if (this.isHydratingCeilingSetup) return;
            if (!newVal || newVal <= 0) {
                // User deleted or cleared the value → reset products
                this.productsNeedRecalculation = false; // no recalculation needed anymore
                this.isProductsChanged = false;
            } else {
                // Value changed (but is valid) → require recalculation
                this.productsNeedRecalculation = true;
            }
        },
        "ceilingSetup.floors": function (newVal) {
            if (this.isHydratingCeilingSetup) return;
            if (!newVal || newVal <= 0) {
                this.productsNeedRecalculation = false;
                this.isProductsChanged = false;
            } else {
                this.productsNeedRecalculation = true;
            }
        },
    },
};
</script>
